import React, { useState, useEffect } from "react";
import { getMonedas, Moneda } from "../../services/api";

const API_URL = "https://api-kyb-web-h7ghg6hse3hrd8e9.brazilsouth-01.azurewebsites.net";

interface EmpresaData {
  id: number;
  nombre: string;
  razonSocial: string | null;
  rut: string | null;
  domicilio1: string | null;
  domicilio2: string | null;
  domicilio3: string | null;
  natJur: string | null;
  telefono: string | null;
  email: string | null;
  giro: string | null;
  bps: string | null;
  bse: string | null;
  codMon1: string | null;
  codMon2: string | null;
  inicioEj: string | null;
  fechaIngreso: boolean;
  usuarioIngreso: boolean;
  diasAudit: number | null;
  activo: boolean;
}

interface Props {
  empresaId: number;
  token: string;
  puedeModificar: boolean;
}

type Solapa = "general" | "datos" | "organismos" | "auditoria";

export default function Empresa({ empresaId, token, puedeModificar }: Props) {
  const [empresa, setEmpresa] = useState<EmpresaData | null>(null);
  const [editando, setEditando] = useState<EmpresaData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [solapa, setSolapa] = useState<Solapa>("general");
  const [monedas, setMonedas] = useState<Moneda[]>([]);

  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      const response = await fetch(`${API_URL}/api/Empresas/${empresaId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmpresa(data);
        setEditando({ ...data });
      }
      const monedasData = await getMonedas(empresaId, token);
      setMonedas(monedasData);
      setCargando(false);
    };
    cargar();
  }, [empresaId]);

  const handleGuardar = async () => {
    if (!editando) return;
    setGuardando(true);
    setMensajeOk("");
    setMensajeError("");
    const response = await fetch(`${API_URL}/api/Empresas/${empresaId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify(editando),
    });
    if (response.ok) {
      setEmpresa({ ...editando });
      setMensajeOk("Guardado correctamente");
      setTimeout(() => setMensajeOk(""), 2000);
    } else {
      setMensajeError("Error al guardar");
    }
    setGuardando(false);
  };

  const handleCancelar = () => {
    if (empresa) setEditando({ ...empresa });
    setMensajeOk("");
    setMensajeError("");
  };

  if (cargando) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Cargando...</div>;
  if (!editando) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>No se encontró la empresa.</div>;

  const campo = (label: string, valor: string | null, campoKey: keyof EmpresaData, readOnly = false) => (
    <div style={styles.campo}>
      <label style={styles.campoLabel}>{label}</label>
      <input
        style={{ ...styles.campoInput, backgroundColor: readOnly ? "#f4f6f8" : "#fff", color: readOnly ? "#888" : "#333" }}
        value={valor || ""}
        readOnly={readOnly || !puedeModificar}
        onChange={e => setEditando({ ...editando!, [campoKey]: e.target.value })}
      />
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.titulo}>{empresa?.nombre}</div>
          <div style={styles.subtitulo}>{empresa?.razonSocial || ""}</div>
        </div>
        {puedeModificar && (
          <div style={{ display: "flex", gap: 8 }}>
            <button style={styles.btnGuardar} onClick={handleGuardar} disabled={guardando}>
              {guardando ? "Guardando..." : "Guardar"}
            </button>
            <button style={styles.btnCancelar} onClick={handleCancelar}>Cancelar</button>
          </div>
        )}
      </div>

      {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
      {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}

      <div style={styles.solapas}>
        {(["general", "datos", "organismos", "auditoria"] as Solapa[]).map(s => (
          <button key={s} style={{ ...styles.solapa, ...(solapa === s ? styles.solapaActiva : {}) }}
            onClick={() => setSolapa(s)}>
            {s === "general" ? "General" : s === "datos" ? "Datos" : s === "organismos" ? "Organismos" : "Auditoría"}
          </button>
        ))}
      </div>

      <div style={styles.solapaContenido}>
        {solapa === "general" && (
          <div style={styles.grilla}>
            {campo("Nombre", editando.nombre, "nombre")}
            {campo("Razón social", editando.razonSocial, "razonSocial")}
            {campo("Naturaleza jurídica", editando.natJur, "natJur")}
            {campo("Giro principal", editando.giro, "giro")}
            {campo("Domicilio (línea 1)", editando.domicilio1, "domicilio1")}
            {campo("Domicilio (línea 2)", editando.domicilio2, "domicilio2")}
            {campo("Domicilio (línea 3)", editando.domicilio3, "domicilio3")}
            {campo("Teléfono", editando.telefono, "telefono")}
            {campo("Email", editando.email, "email")}
          </div>
        )}

        {solapa === "datos" && (
          <div style={styles.grilla}>
            <div style={styles.campo}>
              <label style={styles.campoLabel}>Moneda principal</label>
              <select style={styles.campoInput}
                value={editando.codMon1 || ""}
                disabled={!puedeModificar}
                onChange={e => setEditando({ ...editando!, codMon1: e.target.value })}>
                <option value="">— Seleccionar —</option>
                {monedas.map(m => (
                  <option key={m.codigo} value={m.codigo}>{m.codigo} — {m.nombre}</option>
                ))}
              </select>
            </div>
            <div style={styles.campo}>
              <label style={styles.campoLabel}>Moneda secundaria</label>
              <select style={styles.campoInput}
                value={editando.codMon2 || ""}
                disabled={!puedeModificar}
                onChange={e => setEditando({ ...editando!, codMon2: e.target.value })}>
                <option value="">— Seleccionar —</option>
                {monedas.map(m => (
                  <option key={m.codigo} value={m.codigo}>{m.codigo} — {m.nombre}</option>
                ))}
              </select>
            </div>
            <div style={styles.campo}>
              <label style={styles.campoLabel}>Inicio del ejercicio actual</label>
              <input style={styles.campoInput} type="date"
                value={editando.inicioEj ? editando.inicioEj.split("T")[0] : ""}
                readOnly={!puedeModificar}
                onChange={e => setEditando({ ...editando!, inicioEj: e.target.value })} />
            </div>
          </div>
        )}

        {solapa === "organismos" && (
          <div style={styles.grilla}>
            {campo("RUT", editando.rut, "rut")}
            {campo("BPS", editando.bps, "bps")}
            {campo("BSE", editando.bse, "bse")}
          </div>
        )}

        {solapa === "auditoria" && (
          <div style={styles.grilla}>
            <div style={styles.campo}>
              <label style={styles.campoLabel}>Mantener registros de auditoría (días)</label>
              <input style={styles.campoInput} type="number"
                value={editando.diasAudit ?? 90}
                readOnly={!puedeModificar}
                onChange={e => setEditando({ ...editando!, diasAudit: Number(e.target.value) })} />
            </div>
            <div style={styles.campoCheck}>
              <input type="checkbox"
                checked={editando.fechaIngreso}
                disabled={!puedeModificar}
                onChange={e => setEditando({ ...editando!, fechaIngreso: e.target.checked })} />
              <label style={styles.campoLabelCheck}>Guardar fecha y hora en que se ingresó el asiento</label>
            </div>
            <div style={styles.campoCheck}>
              <input type="checkbox"
                checked={editando.usuarioIngreso}
                disabled={!puedeModificar}
                onChange={e => setEditando({ ...editando!, usuarioIngreso: e.target.checked })} />
              <label style={styles.campoLabelCheck}>Guardar el nombre del usuario que ingresa el asiento</label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden", maxWidth: 800 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 28px", borderBottom: "1px solid #f0f0f0" },
  titulo: { fontSize: 18, fontWeight: 700, color: "#1a1a2e" },
  subtitulo: { fontSize: 13, color: "#888", marginTop: 2 },
  solapas: { display: "flex", borderBottom: "2px solid #e0e0e0", padding: "0 28px" },
  solapa: { padding: "10px 16px", border: "none", background: "transparent", fontSize: 13, cursor: "pointer", color: "#888", borderBottom: "2px solid transparent", marginBottom: -2 },
  solapaActiva: { color: "#2B8FCC", fontWeight: 600, borderBottom: "2px solid #2B8FCC" },
  solapaContenido: { padding: "24px 28px" },
  grilla: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" },
  campo: { marginBottom: 16 },
  campoLabel: { display: "block", fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 4 },
  campoInput: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" as const, outline: "none" },
  campoCheck: { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, gridColumn: "1 / -1" },
  campoLabelCheck: { fontSize: 13, color: "#333", lineHeight: 1.4 },
  btnGuardar: { padding: "8px 20px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnCancelar: { padding: "8px 20px", backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  mensajeOk: { backgroundColor: "#e8f8f0", color: "#27ae60", padding: "10px 28px", fontSize: 13 },
  mensajeError: { backgroundColor: "#fff0f0", color: "#e74c3c", padding: "10px 28px", fontSize: 13 },
};